import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useCart } from '@/contexts/CartContext';
import { Download, RefreshCw, Headphones, Check, X as XIcon, FileCode, ExternalLink } from 'lucide-react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

export default function ProductDetailPage() {
  const { slug } = useParams();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const { addItem, items } = useCart();

  useEffect(() => {
    setLoading(true);
    axios.get(`${API}/api/products/${slug}`)
      .then(r => setProduct(r.data))
      .catch(() => setProduct(null))
      .finally(() => setLoading(false));
  }, [slug]);

  const handleBuy = async () => {
    try {
      const { data } = await axios.post(`${API}/api/checkout/single`, {
        product_slug: slug,
        origin_url: window.location.origin,
        affiliate_code: localStorage.getItem('ref_code') || null
      }, { withCredentials: true });
      if (data.url) window.location.href = data.url;
    } catch (err) {
      if (err.response?.status === 401) {
        window.location.href = '/login';
      }
    }
  };

  if (loading) return <div className="min-h-screen pt-24 flex items-center justify-center"><div className="w-6 h-6 border-2 border-amber-400 border-t-transparent rounded-full animate-spin" /></div>;
  if (!product) return <div className="min-h-screen pt-24 flex items-center justify-center text-white/40">Product not found</div>;

  const inCart = items.some(i => i.slug === product.slug);

  return (
    <div className="min-h-screen pt-24 pb-20 px-4 sm:px-6 lg:px-8" data-testid="product-detail-page">
      <div className="mx-auto max-w-6xl">
        {/* Breadcrumb */}
        <nav className="flex items-center gap-2 text-xs text-white/40 mb-6" data-testid="breadcrumb">
          <Link to="/" className="hover:text-white/60">Home</Link>
          <span>/</span>
          <Link to="/products" className="hover:text-white/60">Products</Link>
          <span>/</span>
          <span className="text-white/60">{product.name}</span>
        </nav>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10">
          {/* Image */}
          <div className="relative rounded-xl overflow-hidden border border-white/[0.06]">
            <img src={product.image_url} alt={product.name} className="w-full aspect-[4/3] object-cover" />
            <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent" />
            <div className="absolute top-4 left-4 flex gap-2">
              <span className="px-2.5 py-1 rounded text-xs font-semibold bg-amber-400/90 text-black">{product.platform}</span>
              <span className="px-2.5 py-1 rounded text-xs font-semibold bg-white/10 text-white/80">{product.badge}</span>
            </div>
          </div>

          {/* Info */}
          <div>
            <h1 className="text-3xl font-bold text-white" data-testid="product-name">{product.name}</h1>
            <div className="mt-3 flex items-baseline gap-3">
              <span className="text-3xl font-bold text-white" data-testid="product-price">${product.price}</span>
              <span className="text-sm text-white/30">USD / Lifetime License</span>
              {product.original_price && (
                <span className="text-lg text-white/30 line-through">${product.original_price}</span>
              )}
            </div>
            <p className="mt-4 text-sm text-white/50 leading-relaxed">{product.description}</p>

            <div className="mt-6 flex gap-3">
              <button
                onClick={() => addItem(product)}
                disabled={inCart}
                className={`flex-1 py-3 rounded-lg text-sm font-semibold transition-colors ${inCart ? 'bg-white/5 text-white/30' : 'bg-amber-400 text-black hover:bg-amber-300'}`}
                data-testid="add-to-cart-btn"
              >
                {inCart ? 'Already in Cart' : 'Add to Cart'}
              </button>
            </div>

            {/* Download file info */}
            {product.download_url && (
              <div className="mt-5 rounded-lg border border-emerald-400/20 bg-emerald-400/[0.04] p-4" data-testid="download-info">
                <div className="flex items-center gap-3">
                  <div className="flex items-center justify-center w-10 h-10 rounded-lg bg-emerald-400/10">
                    <FileCode size={20} className="text-emerald-400" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-white/80">{product.download_filename || 'Source Code'}</p>
                    <p className="text-[11px] text-white/40">MQ5 source file — included with purchase</p>
                  </div>
                  <a
                    href={product.download_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium bg-emerald-400/10 text-emerald-400 border border-emerald-400/20 hover:bg-emerald-400/20 transition-colors"
                    data-testid="preview-download-btn"
                  >
                    <ExternalLink size={12} /> Preview File
                  </a>
                </div>
              </div>
            )}

            <div className="mt-6 flex items-center gap-6 text-xs text-white/40">
              <span className="flex items-center gap-1.5"><Download size={14} /> Instant Download</span>
              <span className="flex items-center gap-1.5"><RefreshCw size={14} /> Lifetime Updates</span>
              <span className="flex items-center gap-1.5"><Headphones size={14} /> 24/7 Support</span>
            </div>

            {/* Features */}
            <div className="mt-8">
              <h3 className="text-sm font-semibold text-white/80 mb-3">Features</h3>
              <ul className="space-y-2">
                {product.features?.map((f, i) => (
                  <li key={i} className="flex items-start gap-2 text-sm text-white/50">
                    <Check size={14} className="text-amber-400 mt-0.5 shrink-0" />
                    <span>{f}</span>
                  </li>
                ))}
              </ul>
              {product.features_excluded?.length > 0 && (
                <ul className="mt-3 space-y-2">
                  {product.features_excluded.map((f, i) => (
                    <li key={i} className="flex items-start gap-2 text-sm text-white/30">
                      <XIcon size={14} className="text-white/20 mt-0.5 shrink-0" />
                      <span className="line-through">{f}</span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        </div>

        {/* Long description */}
        {product.long_description && (
          <div className="mt-14 max-w-3xl">
            <h2 className="text-xl font-bold text-white mb-4">About This EA</h2>
            <div className="prose prose-invert prose-sm text-white/50 leading-relaxed whitespace-pre-line">
              {product.long_description}
            </div>
          </div>
        )}

        {/* System Requirements */}
        {product.system_requirements && (
          <div className="mt-12">
            <h2 className="text-xl font-bold text-white mb-4">System Requirements</h2>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
              {Object.entries(product.system_requirements).map(([key, val]) => (
                <div key={key} className="rounded-lg border border-white/[0.06] bg-white/[0.02] p-4">
                  <p className="text-xs text-white/30 uppercase tracking-wider mb-1">{key.replace(/_/g, ' ')}</p>
                  <p className="text-sm text-white/70">{val}</p>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
